// import { useEffect } from 'react';
// import { useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import { useAppSelector } from '../redux/hooks';
import GeneralNavbar from '../components/navbar/GeneralNavbar';
import Footer from "../components/footer/Footer";
import { CheckCircle, Clock } from 'lucide-react';
import { formatInternationalNumber } from '../data/countryCodes';

const ICICI_CBDC_APP_URL = 'https://play.google.com/store/apps/details?id=com.icici.digitalrupee&hl=en';

function SuccessPage(): JSX.Element | null {
  const { voucher, activationUserDetails } = useAppSelector((state: any) => state.voucher);

  if (activationUserDetails === null || voucher === null) {
    return null;
  }

  const isCbdc = voucher.brand === 'ICICI_CBDC';
  const isMyp = voucher.brand === 'MYP';
  const isPending = activationUserDetails.status === 'pending';

  // Everything below is read from the voucher's LoB (currency, timezone,
  // locale), so nothing here assumes India or Malaysia.
  const currency = voucher.display_currency;
  const locale = currency?.locale || 'en-IN';
  const symbol = currency?.symbol || '₹';
  const voucherValue = `${symbol}${Number(voucher.final_balance ?? 0).toLocaleString(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
  const expiryRaw = activationUserDetails.expiryDate || voucher.expiryDate;
  const expiry = expiryRaw ? new Date(expiryRaw) : null;
  const validUntil =
    expiry && !Number.isNaN(expiry.getTime())
      ? expiry.toLocaleDateString(locale, {
          day: 'numeric',
          month: 'long',
          year: 'numeric',
          timeZone: voucher.display_timezone?.iana,
        })
      : null;
  const issuedTo = formatInternationalNumber(activationUserDetails.number);

  return (
    <div className="dotted-background min-h-screen">
      <GeneralNavbar logo={voucher.program.logo} />

      <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 flex flex-col">
       
         
          <div className="px-8 py-6 flex justify-center items-center">
            <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md border border-gray-100">
              <div className="text-center mb-6">
                {isPending ? (
                  <Clock className="w-16 h-16 text-amber-500 mx-auto mb-4" />
                ) : (
                  <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
                )}
              </div>

              {isMyp && !isPending ? (
                <div className="mb-6">
                  <h1 className="text-center text-2xl font-semibold text-gray-900">
                    Your voucher is ready
                  </h1>
                  <p className="text-center text-sm text-gray-600 mt-2">
                    {voucher.program?.displayName ? `${voucher.program.displayName} has` : 'Your voucher has'} been
                    issued to your mobile number and can be used straight away.
                  </p>

                  <dl className="mt-6 rounded-xl border border-gray-200 bg-gray-50 divide-y divide-gray-200 text-sm">
                    <div className="flex items-center justify-between px-4 py-3">
                      <dt className="text-gray-500">Voucher value</dt>
                      <dd className="font-semibold text-gray-900">{voucherValue}</dd>
                    </div>
                    <div className="flex items-center justify-between px-4 py-3">
                      <dt className="text-gray-500">Issued to</dt>
                      <dd className="font-semibold text-gray-900">{issuedTo}</dd>
                    </div>
                    {validUntil && (
                      <div className="flex items-center justify-between px-4 py-3">
                        <dt className="text-gray-500">Valid until</dt>
                        <dd className="font-semibold text-gray-900">{validUntil}</dd>
                      </div>
                    )}
                  </dl>

                  <div className="mt-6">
                    <h2 className="text-sm font-semibold text-gray-900">How to use your voucher</h2>
                    <ol className="mt-2 space-y-2 text-sm text-gray-600 list-decimal list-inside">
                      <li>Open the MYP wallet app and sign in with the mobile number above.</li>
                      <li>Your voucher will appear in your wallet.</li>
                      <li>At a participating merchant, scan their DuitNow QR code, confirm the amount and enter your PIN to pay.</li>
                    </ol>
                    <p className="mt-4 text-xs text-gray-500">
                      Can&apos;t see your voucher? Make sure you signed in with the same mobile number, or contact the issuer.
                    </p>
                  </div>
                </div>
              ) : (
              <div className="text-center text-gray-700 mb-6">
                {isPending ? (
                  <>
                    We're still processing your request. This can take a little longer than usual -
                    we'll notify you as soon as your {isCbdc ? 'Digital Rupee' : 'voucher'} is ready to use.
                  </>
                ) : isCbdc ? (
                  <>
                    Your Digital Rupee has been issued to {activationUserDetails.number}. Install
                    the Digital Rupee by ICICI Bank app to start using it.
                  </>
                ) : (
                  <>
                    Voucher issued successfully and sent to {activationUserDetails.number}.
                  </>
                )}
              </div>
              )}

              {isCbdc && !isPending && (
                <div className="text-center mb-6">
                  <a
                    href={ICICI_CBDC_APP_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block px-6 py-3 text-sm font-semibold text-white bg-[#53978e] rounded-lg hover:bg-[#43786f] transition-colors"
                  >
                    Download Digital Rupee by ICICI Bank
                  </a>
                </div>
              )}

              {activationUserDetails?.bankLogo && (
                <div className="text-center">
                  <div className="text-sm text-gray-500 mb-2">
                    {isCbdc ? 'Issued by' : 'Voucher issued by'}
                  </div>
                  <div className="w-40 h-10 overflow-hidden rounded-lg">
                    <img
                      src={activationUserDetails.bankLogo}
                      alt="Issuer Bank"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        
      </main>
      
      <Footer />
    </div>
  );
}

export default SuccessPage; 